import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { articles } from "@/data/articles";
import { useLanguage } from "@/lib/i18n";

interface RelatedArticlesProps {
  /** Slug of the current article, so it is excluded from the list. */
  currentSlug: string;
}

export function RelatedArticles({ currentSlug }: RelatedArticlesProps) {
  const { language } = useLanguage();

  const others = articles.filter((article) => article.slug !== currentSlug);

  if (others.length === 0) return null;

  const heading = language === "fr" ? "À lire ensuite" : "Read next";
  const description =
    language === "fr"
      ? "Continuez votre lecture avec ces articles connexes."
      : "Keep reading with these related articles.";

  return (
    <section className="py-12 md:py-16 border-t border-border/30">
      <div className="mx-auto max-w-4xl px-4 sm:px-6">
        <span className="inline-block text-xs font-bold uppercase tracking-[0.18em] text-primary mb-3">
          {heading}
        </span>
        <p className="text-base text-muted-foreground mb-8 max-w-xl leading-relaxed">
          {description}
        </p>

        <div className="grid gap-5 sm:grid-cols-2">
          {others.map((article) => {
            const meta = article[language];
            // Use the explicit localized paths: EN slugs differ from FR slugs.
            const href = language === "fr" ? article.frPath : article.enPath;

            return (
              <Link
                key={article.slug}
                to={href}
                className="group flex flex-col rounded-2xl border border-border/40 bg-card overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                <div className="overflow-hidden h-40">
                  <img
                    src={article.image}
                    alt={meta.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>

                <div className="flex flex-col flex-1 p-5">
                  <span className="inline-block text-xs font-bold uppercase tracking-[0.15em] text-primary mb-3">
                    {meta.category}
                  </span>

                  <h3 className="font-['Source_Serif_4'] text-lg leading-snug tracking-tight text-foreground font-semibold mb-2 group-hover:text-primary transition-colors duration-200">
                    {meta.title}
                  </h3>

                  <p className="text-sm leading-relaxed text-muted-foreground line-clamp-2 mb-4">
                    {meta.subtitle}
                  </p>

                  <span className="mt-auto inline-flex items-center gap-1.5 text-sm font-semibold text-primary group-hover:gap-2.5 transition-all duration-200">
                    {language === "fr" ? "Lire l'article" : "Read article"}
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
